/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react';
import {
  Button, Form, Table, ProgressBar,
} from 'react-bootstrap';
import { formatBookingDate } from '../../../Utils/timezone';
import thousandSeparator from '../../../Utils/numberFormat';
import { isExpenseBooking } from '../../../Utils/bookingType';

function OrderInputPreview({
  order,
  loading,
  handleSubmit,
  partners,
  onBack,
  syncOptions = {},
  linkedBookingsCount = 0,
  syncProgress = { current: 0, total: 0, syncing: false },
}) {
  const [sendNotificationToCustomer, setSendNotificationToCustomer] = useState(false);

  const getPartnerName = (partnerId) => {
    const orderPartner = partners.find((partner) => partner.Id === partnerId);

    if (orderPartner) {
      return orderPartner.name;
    }

    return '';
  };

  return (
    <>
      <Table striped className="order-preview-table">
        <tbody>
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>Partner Information</th>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Partner Name
            </td>
            <td>{getPartnerName(order.partner_id)}</td>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Partner #
            </td>
            <td>{order.partner_booking_reference}</td>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Partner Cancellation Compensation
            </td>
            <td>
              {thousandSeparator(order.partner_cancellation_price)}
              {' '}
              {order.partner_cancellation_currency}
            </td>
          </tr>
          {/* Event Details */}
          {(order.is_event_booking || order.event_name) && (
            <>
              <tr>
                <th className="bg-warning-subtle" colSpan={2}>
                  <i className="fa fa-calendar-alt me-2" />
                  Event Details
                </th>
              </tr>
              <tr>
                <td className="bg-body-secondary">Event Name</td>
                <td>{order.event_name}</td>
              </tr>
              {order.event_group && (
                <tr>
                  <td className="bg-body-secondary">Event Group</td>
                  <td>{order.event_group}</td>
                </tr>
              )}
              {order.passenger_manifest && order.passenger_manifest.length > 0 && (
                <tr>
                  <td className="bg-body-secondary">Passenger Manifest</td>
                  <td>
                    <ul className="mb-0 ps-3">
                      {order.passenger_manifest.map((p, idx) => (
                        <li key={`manifest-${idx + 1}`}>
                          {p.name}
                          {p.flight_number && ` (${p.flight_number})`}
                          {p.notes && ` - ${p.notes}`}
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              )}
            </>
          )}

          <tr>
            <th className="bg-dark-subtle" colSpan={2}>Passenger Information</th>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Full Name
            </td>
            <td>{order.full_name}</td>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              E-mail address
            </td>
            <td>{order.email_address}</td>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Phone Number
            </td>
            <td>{order.phone_number}</td>
          </tr>
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>Travel Information</th>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Date
            </td>
            <td>{order.date_datetime && formatBookingDate(order, 'date_datetime')}</td>
          </tr>
          {order.return_date_datetime && (
            <tr>
              <td className="bg-body-secondary">
                Return Date
                {' '}
                <small className="text-muted">(Legacy)</small>
              </td>
              <td>{formatBookingDate(order, 'return_date_datetime')}</td>
            </tr>
          )}
          <tr>
            <td className="bg-body-secondary">
              From
            </td>
            <td>{order.destination_from?.address}</td>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              To
            </td>
            <td>{order.destination_to?.address}</td>
          </tr>
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>Programme</th>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Programme Total Duration (h)
            </td>
            <td>{order.programme?.total_hours}</td>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Programme Name
            </td>
            <td>{order.programme?.name}</td>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Programme Itinerary
            </td>
            <td>{order.programme?.itinerary}</td>
          </tr>
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>Other Information</th>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Flight Number
            </td>
            <td>{order.flight_number}</td>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Comments
            </td>
            <td>{order.comments}</td>
          </tr>

          {/* Passengers */}
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>Passengers</th>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Total Amount
            </td>
            <td>
              {order.passengers.amount}
            </td>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Adults
            </td>
            <td>
              {order.passengers.adults}
            </td>
          </tr>
          {order.booking_type === 'experience' ? (
            <tr>
              <td className="bg-body-secondary">Children</td>
              <td>
                {order.passengers.children || 0}
              </td>
            </tr>
          ) : (
            <>
              <tr>
                <td className="bg-body-secondary">Convertible Seat</td>
                <td>
                  {order.passengers.convertible_seat}
                </td>
              </tr>
              <tr>
                <td className="bg-body-secondary">Booster Seat</td>
                <td>
                  {order.passengers.booster_seat}
                </td>
              </tr>
              <tr>
                <td className="bg-body-secondary">Infant Carrier</td>
                <td>
                  {order.passengers.infant_carrier}
                </td>
              </tr>
            </>
          )}

          {/* Order Details */}
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>Order Details</th>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Order Status
            </td>
            <td>
              {order.status}
            </td>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Vehicle Category
            </td>
            <td>
              {order.category.name}
            </td>
          </tr>

          {/* Price */}
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>Price</th>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Final Price
            </td>
            <td>
              {thousandSeparator(order.price)}
              {' '}
              {order.currency}
            </td>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Operator Cancellation Compensation
            </td>
            <td>
              {thousandSeparator(order.operator_cancellation_price)}
              {' '}
              {order.operator_cancellation_currency}
            </td>
          </tr>
        </tbody>
      </Table>

      {/* Linked Bookings Sync Summary */}
      {linkedBookingsCount > 0 && Object.values(syncOptions).some((v) => v) && (
        <div className="alert alert-info mb-3">
          <i className="fa fa-link me-2" />
          <strong>Linked Bookings Sync:</strong>
          {' '}
          {syncProgress.syncing ? (
            <>
              Syncing linked bookings...
              <ProgressBar
                now={(syncProgress.current / syncProgress.total) * 100}
                label={`${syncProgress.current} / ${syncProgress.total}`}
                className="mt-2"
                animated
              />
            </>
          ) : (
            <>
              The following fields will be synced to
              {' '}
              {linkedBookingsCount}
              {' '}
              linked booking(s):
              {' '}
              <strong>
                {Object.entries(syncOptions)
                  .filter(([, enabled]) => enabled)
                  .map(([field]) => {
                    const labels = {
                      full_name: 'Passenger Name',
                      email_address: 'Email',
                      phone_number: 'Phone',
                      status: 'Status',
                    };
                    return labels[field] || field;
                  })
                  .join(', ')}
              </strong>
            </>
          )}
        </div>
      )}

      <div className="sticky-modal-footer">
        {/* There is no customer on an expense, and the API REFUSES a save
            carrying `send_notification_to_customer: true`. Taking the
            checkbox away is what stops the office hitting that refusal with
            no idea why; `me-auto` moves to the Back button so the footer
            still lays out. */}
        {!isExpenseBooking(order) && (
        <Form.Group controlId="send_notification_to_customer" className="d-flex align-items-center me-auto">
          <Form.Label className="mb-0">Send notification to customer</Form.Label>
          <Form.Check
            type="checkbox"
            className="ms-2"
            disabled={!order.email_address}
            checked={sendNotificationToCustomer}
            onChange={() => setSendNotificationToCustomer(!sendNotificationToCustomer)}
          />
        </Form.Group>
        )}

        <Button
          variant="secondary"
          className={isExpenseBooking(order) ? 'me-auto' : undefined}
          onClick={onBack}
        >
          Back to edit
        </Button>

        <Button
          onClick={() => handleSubmit(!isExpenseBooking(order) && sendNotificationToCustomer)}
          disabled={loading}
        >
          <i className="fa fa-thumbs-up" />
          { ' ' }
          { order._id ? 'Modify' : 'Create' }
          { ' ' }
          { sendNotificationToCustomer && '& email' }
        </Button>
      </div>
    </>
  );
}

export default OrderInputPreview;
